import { useState, useCallback } from 'react';

export interface RecentItem {
  id: string;
  type: 'request' | 'mission' | 'schedule' | 'patient';
  label: string;
  path: string;
  timestamp: number;
}

const STORAGE_KEY = 'camo-recent-items';
const MAX_ITEMS = 10;

function readFromStorage(): RecentItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as RecentItem[];
  } catch {
    return [];
  }
}

function writeToStorage(items: RecentItem[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
}

function useRecentItems() {
  const [items, setItems] = useState<RecentItem[]>(() => readFromStorage());

  const addRecentItem = useCallback((item: Omit<RecentItem, 'timestamp'>) => {
    setItems(prev => {
      // Remove duplicate (same id + type)
      const filtered = prev.filter(
        existing => !(existing.id === item.id && existing.type === item.type)
      );

      const newItem: RecentItem = {
        ...item,
        timestamp: Date.now(),
      };

      // Prepend and cap at MAX_ITEMS
      const updated = [newItem, ...filtered].slice(0, MAX_ITEMS);
      writeToStorage(updated);
      return updated;
    });
  }, []);

  const getRecentItems = useCallback((): RecentItem[] => {
    // Already sorted by most recent (prepend strategy), but ensure order
    return [...items].sort((a, b) => b.timestamp - a.timestamp);
  }, [items]);

  const clearRecentItems = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setItems([]);
  }, []);

  return { items, addRecentItem, getRecentItems, clearRecentItems };
}

export default useRecentItems;
